import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Search,
    Check,
    Loader2,
    CheckSquare,
    Square,
    Plus,
    Sparkles,
    X,
    Weight,
} from 'lucide-react';
import { getPageviewUrlsByDomain } from '@/services/api/pageview-report';
import { useToast } from '@/hooks/use-toast';
import type { AllowedPath } from '@/types/redirect-group';
import {
    buildApiPathSet,
    detectManualPaths,
    mergeUrlEntries,
    normalizePath,
    resolveManualAdd,
} from './urlSelectorLogic';

interface UrlSelectorDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    domain: string;
    selectedPaths: AllowedPath[];
    onConfirm: (paths: AllowedPath[]) => void;
}

function getDefaultDates(): { start: string; end: string } {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    return { start: fmt(start), end: fmt(end) };
}

export default function UrlSelectorDialog({
    open,
    onOpenChange,
    domain,
    selectedPaths,
    onConfirm,
}: UrlSelectorDialogProps) {
    const { toast } = useToast();
    const [search, setSearch] = useState('');
    // Map de path selecionado -> weight (null = usa RPS, number = fixo)
    const [selected, setSelected] = useState<Map<string, number | null>>(new Map());
    // Paths manuais (nao existem na API)
    const [manualPaths, setManualPaths] = useState<Set<string>>(new Set());
    const [manualInput, setManualInput] = useState('');
    const [manualWeight, setManualWeight] = useState<string>('1');
    const { start, end } = getDefaultDates();

    // Reseta selection quando abre
    useEffect(() => {
        if (open) {
            const initial = new Map<string, number | null>();
            // Normaliza igual a lista, senao um salvo como "/foo" nao casa
            // com "/foo/" da API e aparece duplicado
            selectedPaths.forEach((p) => {
                initial.set(normalizePath(p.path) || p.path, p.weight ?? null);
            });
            setSelected(initial);
            setManualPaths(new Set());
            setSearch('');
            setManualInput('');
            setManualWeight('1');
        }
    }, [open, selectedPaths]);

    const { data: urls = [], isLoading } = useQuery({
        queryKey: ['pageview-urls', domain, start, end],
        queryFn: () => getPageviewUrlsByDomain(domain, start, end, undefined, 2000),
        enabled: open && !!domain,
        staleTime: 1000 * 60 * 5,
    });

    // Paths que vieram da API (normalizados) - fonte unica pra checar duplicata
    const apiPathSet = useMemo(() => buildApiPathSet(urls), [urls]);

    // Detecta paths manuais ja existentes (selectedPaths que nao estao na API)
    useEffect(() => {
        if (!open || urls.length === 0) return;
        setManualPaths(detectManualPaths(selectedPaths, apiPathSet));
    }, [open, urls, apiPathSet, selectedPaths]);

    // Lista combinada: paths manuais + paths da API, UMA entrada por path.
    // Se um path manual tambem veio da API, a entrada da API (post_id +
    // pageviews) prevalece - nao pode aparecer duas vezes.
    const allPaths = useMemo(() => mergeUrlEntries(urls, manualPaths), [urls, manualPaths]);

    const filteredUrls = useMemo(() => {
        if (!search.trim()) return allPaths;
        const s = search.toLowerCase();
        return allPaths.filter(
            (u) =>
                u.path.toLowerCase().includes(s) ||
                (u.post_id || '').toLowerCase().includes(s)
        );
    }, [allPaths, search]);

    const toggle = (path: string, isManual: boolean) => {
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(path)) {
                next.delete(path);
            } else {
                // Manual default = 1, API default = null (usa RPS)
                next.set(path, isManual ? 1 : null);
            }
            return next;
        });
    };

    const updateWeight = (path: string, weight: number | null) => {
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(path)) {
                next.set(path, weight);
            }
            return next;
        });
    };

    const selectAll = () => {
        setSelected((prev) => {
            const next = new Map(prev);
            filteredUrls.forEach((u) => {
                if (!next.has(u.path)) {
                    next.set(u.path, u.manual ? 1 : null);
                }
            });
            return next;
        });
    };

    const clearAll = () => {
        setSelected(new Map());
    };

    const handleAddManual = () => {
        const result = resolveManualAdd(manualInput, manualWeight, apiPathSet, manualPaths);

        if (result.kind === 'invalid_path') {
            toast({
                variant: 'destructive',
                title: 'Path invalido',
                description: 'Digite um path valido tipo /minha-pagina/',
            });
            return;
        }
        if (result.kind === 'invalid_weight') {
            toast({
                variant: 'destructive',
                title: 'Peso invalido',
                description: 'Peso precisa ser um numero maior que 0',
            });
            return;
        }

        const { path, weight } = result;

        // Em qualquer caso o peso manual explicito prevalece sobre o auto (RPS)
        setSelected((prev) => {
            const next = new Map(prev);
            next.set(path, weight);
            return next;
        });
        setManualInput('');
        setManualWeight('1');

        if (result.kind === 'exists_auto') {
            // Ja existe como automatica (tem trafego): nao cria entrada
            // manual, so seleciona a existente com o peso informado
            toast({
                title: 'URL ja estava na lista',
                description: `${path} ja tem trafego - peso ${weight} aplicado na entrada existente`,
            });
            return;
        }
        if (result.kind === 'exists_manual') {
            toast({
                title: 'Peso atualizado',
                description: `${path} ja era manual - peso ${weight}`,
            });
            return;
        }

        setManualPaths((prev) => new Set(prev).add(path));
        toast({
            title: 'Path adicionado',
            description: `${path} (peso ${weight})`,
        });
    };

    const removeManual = (path: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setManualPaths((prev) => {
            const next = new Set(prev);
            next.delete(path);
            return next;
        });
        setSelected((prev) => {
            const next = new Map(prev);
            next.delete(path);
            return next;
        });
    };

    const handleManualKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddManual();
        }
    };

    const handleConfirm = () => {
        const result: AllowedPath[] = Array.from(selected.entries()).map(
            ([path, weight]) => ({
                path,
                weight: weight ?? null,
            })
        );
        onConfirm(result);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Selecionar URLs de {domain}</DialogTitle>
                    <DialogDescription>
                        Escolha as URLs que entrarao no sync. Paths da API usam o RPS calculado.
                        Paths manuais usam o peso customizado.
                    </DialogDescription>
                </DialogHeader>

                {/* Adicionar path manual */}
                <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50/30 p-3 mt-2">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="h-3.5 w-3.5 text-blue-600" />
                        <span className="text-xs font-semibold text-blue-900">
                            Adicionar path manual
                        </span>
                        <span className="text-[10px] text-gray-500">
                            (para URLs novas sem trafego ainda)
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            value={manualInput}
                            onChange={(e) => setManualInput(e.target.value)}
                            onKeyDown={handleManualKeyDown}
                            placeholder="/minha-nova-pagina/"
                            className="h-9 font-mono text-sm flex-1"
                        />
                        <div className="flex items-center gap-1">
                            <Weight className="h-3 w-3 text-gray-500" />
                            <Input
                                type="number"
                                step="0.1"
                                min="0.01"
                                value={manualWeight}
                                onChange={(e) => setManualWeight(e.target.value)}
                                onKeyDown={handleManualKeyDown}
                                placeholder="1"
                                className="h-9 w-20 text-sm"
                                title="Peso do path"
                            />
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            onClick={handleAddManual}
                            disabled={!manualInput.trim()}
                        >
                            <Plus className="mr-1 h-3 w-3" />
                            Adicionar
                        </Button>
                    </div>
                </div>

                {/* Busca e acoes */}
                <div className="flex items-center gap-2 mt-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar por path ou post_id..."
                            className="h-9 pl-9"
                        />
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={selectAll}
                        disabled={filteredUrls.length === 0}
                        title="Selecionar todas as visiveis"
                    >
                        <CheckSquare className="mr-1 h-3 w-3" />
                        Todas
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearAll}
                        disabled={selected.size === 0}
                    >
                        <Square className="mr-1 h-3 w-3" />
                        Limpar
                    </Button>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                    <span>
                        {filteredUrls.length} URLs encontradas ·{' '}
                        <strong>{selected.size}</strong> selecionadas
                        {manualPaths.size > 0 && (
                            <>
                                {' '}
                                · <span className="text-blue-600">{manualPaths.size} manuais</span>
                            </>
                        )}
                    </span>
                    <span>Periodo: ultimos 7 dias</span>
                </div>

                {/* Lista */}
                <div className="flex-1 overflow-y-auto border rounded-lg mt-2 min-h-[260px]">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-full py-20 text-gray-400">
                            <Loader2 className="h-8 w-8 animate-spin mb-2" />
                            <p className="text-sm">Carregando URLs...</p>
                        </div>
                    ) : filteredUrls.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full py-20 text-gray-400">
                            <p className="text-sm">Nenhuma URL encontrada</p>
                            {search && (
                                <p className="text-xs mt-1">Tente outro termo de busca</p>
                            )}
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {filteredUrls.map((url) => {
                                const isSelected = selected.has(url.path);
                                const currentWeight = selected.get(url.path);
                                return (
                                    <div
                                        key={url.path}
                                        className={`flex items-center gap-3 px-3 py-2 hover:bg-blue-50/40 transition ${
                                            isSelected ? 'bg-blue-50/60' : ''
                                        } ${url.manual ? 'bg-amber-50/30' : ''}`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggle(url.path, url.manual)}
                                            className="flex items-center gap-3 flex-1 text-left min-w-0"
                                        >
                                            <div
                                                className={`h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                                    isSelected
                                                        ? 'bg-blue-600 border-blue-600'
                                                        : 'border-gray-300'
                                                }`}
                                            >
                                                {isSelected && <Check className="h-3 w-3 text-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-mono text-sm text-gray-900 truncate">
                                                    {url.path}
                                                </div>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    {url.manual && (
                                                        <Badge
                                                            variant="outline"
                                                            className="text-[9px] py-0 h-4 border-amber-300 bg-amber-50 text-amber-700"
                                                        >
                                                            <Sparkles className="h-2 w-2 mr-0.5" />
                                                            Manual
                                                        </Badge>
                                                    )}
                                                    {url.post_id && (
                                                        <Badge
                                                            variant="outline"
                                                            className="text-[10px] py-0 h-4"
                                                        >
                                                            #{url.post_id}
                                                        </Badge>
                                                    )}
                                                    <span className="text-[10px] text-gray-500">
                                                        {url.manual
                                                            ? 'sem trafego (path novo)'
                                                            : `${url.pageviews.toLocaleString('pt-BR')} pageviews`}
                                                    </span>
                                                </div>
                                            </div>
                                        </button>

                                        {/* Input de peso (apenas se selecionado) */}
                                        {isSelected && (
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                {url.manual ? (
                                                    // Paths manuais SEMPRE tem peso fixo
                                                    <>
                                                        <Weight className="h-3 w-3 text-gray-400" />
                                                        <Input
                                                            type="number"
                                                            step="0.1"
                                                            min="0.01"
                                                            value={currentWeight ?? 1}
                                                            onChange={(e) => {
                                                                const v = parseFloat(e.target.value);
                                                                updateWeight(
                                                                    url.path,
                                                                    isNaN(v) ? 1 : v
                                                                );
                                                            }}
                                                            className="h-7 w-16 text-xs text-center"
                                                            title="Peso customizado"
                                                        />
                                                    </>
                                                ) : (
                                                    // Paths da API - mostra "auto" se sem weight, ou peso custom
                                                    <>
                                                        {currentWeight == null ? (
                                                            <Badge
                                                                variant="outline"
                                                                className="text-[10px] py-0 h-6 cursor-pointer hover:bg-blue-50"
                                                                title="Clique pra definir peso custom"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    updateWeight(url.path, 1);
                                                                }}
                                                            >
                                                                auto (RPS)
                                                            </Badge>
                                                        ) : (
                                                            <>
                                                                <Weight className="h-3 w-3 text-gray-400" />
                                                                <Input
                                                                    type="number"
                                                                    step="0.1"
                                                                    min="0.01"
                                                                    value={currentWeight}
                                                                    onChange={(e) => {
                                                                        const v = parseFloat(e.target.value);
                                                                        updateWeight(
                                                                            url.path,
                                                                            isNaN(v) ? 1 : v
                                                                        );
                                                                    }}
                                                                    className="h-7 w-16 text-xs text-center"
                                                                    title="Peso customizado"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        updateWeight(url.path, null);
                                                                    }}
                                                                    className="text-[9px] text-blue-600 hover:underline ml-1"
                                                                    title="Voltar pra auto"
                                                                >
                                                                    auto
                                                                </button>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Botao remover (apenas manual) */}
                                        {url.manual && (
                                            <button
                                                type="button"
                                                onClick={(e) => removeManual(url.path, e)}
                                                className="flex-shrink-0 p-1 rounded hover:bg-red-100 transition"
                                                title="Remover path manual"
                                            >
                                                <X className="h-3.5 w-3.5 text-red-500" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex gap-3 pt-4 border-t">
                    <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancelar
                    </Button>
                    <Button type="button" className="flex-1" onClick={handleConfirm}>
                        Confirmar ({selected.size} {selected.size === 1 ? 'URL' : 'URLs'})
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
